import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Mail, Gift, MessageCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const NEWSLETTER_KEY = "hostro_newsletter_v1";
const EXIT_KEY = "hostro_exit_intent_v1";
const TOUR_KEY = "hostro_tour_completed_v1";

// Routes where engagement layer (newsletter modal, exit intent, tour) must NEVER render —
// e.g. auth forms where overlays block submit, or full-screen flows like checkout.
const SUPPRESS_ON = ["/login", "/register", "/forgot", "/reset", "/payment"];
const shouldSuppress = (pathname) => SUPPRESS_ON.some(p => pathname.startsWith(p));

/* ---------------------------------------------------------------------------
   Hostro engagement layer:
   • NewsletterPopup — appears after 15s scroll
   • ExitIntentPopup — appears when mouse leaves top of viewport
   • StickyWhatsAppCTA — bottom-right floating button
   • OnboardingTour — 4-step spotlight tour for first-time logged-in users
---------------------------------------------------------------------------- */

export function NewsletterPopup() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  useEffect(() => {
    if (shouldSuppress(loc.pathname)) return;
    if (localStorage.getItem(NEWSLETTER_KEY)) return;
    const t = setTimeout(() => setOpen(true), 15000);
    return () => clearTimeout(t);
  }, [loc.pathname]);
  const close = (dismissed = false) => {
    setOpen(false);
    if (dismissed) localStorage.setItem(NEWSLETTER_KEY, "dismissed");
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", { name: "Newsletter subscriber", email, subject: "Newsletter signup", message: "Wants to receive PG launches in their city.", phone: "" });
      toast.success("You're in! First PG drop coming soon.");
      localStorage.setItem(NEWSLETTER_KEY, "subscribed");
      setOpen(false);
    } catch { toast.error("Failed to subscribe"); }
  };
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center p-4" onClick={()=>close(true)} data-testid="newsletter-popup">
      <div className="brut-card p-6 max-w-md w-full bg-[#FDFCF8] relative" onClick={(e)=>e.stopPropagation()}>
        <button className="absolute top-3 right-3 brut-border p-1 bg-white" onClick={()=>close(true)} data-testid="newsletter-close"><X size={14}/></button>
        <span className="brut-tag pink"><Gift size={10} className="inline mr-1"/> Early access</span>
        <h3 className="font-display font-black text-2xl mt-3">Be first to know when new PGs drop in your city.</h3>
        <p className="text-sm text-[#484848] mt-2">Weekly digest — top PGs, owner deals, food upgrades. No spam.</p>
        <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2">
          <input type="email" required className="brut-input flex-1" placeholder="you@college.edu" value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="newsletter-email"/>
          <button className="brut-btn" data-testid="newsletter-submit"><Mail size={14}/> Subscribe</button>
        </form>
      </div>
    </div>
  );
}

export function ExitIntentPopup() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (shouldSuppress(loc.pathname)) return;
    if (localStorage.getItem(EXIT_KEY)) return;
    const handler = (e) => {
      if (e.clientY <= 0 && !localStorage.getItem(EXIT_KEY)) {
        setOpen(true);
        localStorage.setItem(EXIT_KEY, "shown");
      }
    };
    document.addEventListener("mouseleave", handler);
    return () => document.removeEventListener("mouseleave", handler);
  }, [loc.pathname]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={()=>setOpen(false)} data-testid="exit-popup">
      <div className="brut-card p-6 max-w-lg w-full bg-[#FDFCF8] relative" onClick={(e)=>e.stopPropagation()}>
        <button className="absolute top-3 right-3 brut-border p-1 bg-white" onClick={()=>setOpen(false)} data-testid="exit-close"><X size={14}/></button>
        <span className="brut-tag lime"><Sparkles size={10} className="inline mr-1"/> Wait!</span>
        <h3 className="font-display font-black text-3xl mt-3">Get <span className="bg-[#2ED573] px-1 brut-border">10% off</span> your first month.</h3>
        <p className="text-sm text-[#484848] mt-2">First-time student offer. Book any verified PG and we&apos;ll auto-apply <code className="brut-tag white">HOSTRO10</code> at checkout.</p>
        <div className="flex gap-2 mt-4">
          <Link to="/properties" className="brut-btn flex-1 text-center" data-testid="exit-cta">Browse PGs now</Link>
          <button className="brut-btn white" onClick={()=>setOpen(false)}>Maybe later</button>
        </div>
      </div>
    </div>
  );
}

export function StickyWhatsAppCTA() {
  const [hidden, setHidden] = useState(false);
  if (hidden) return null;
  return (
    <a
      href="https://wa.me/918905290804?text=Hi%20Hostro%2C%20I%20want%20help%20finding%20a%20PG"
      target="_blank" rel="noreferrer"
      className="fixed bottom-5 right-5 z-50 brut-btn !py-3 !px-4 bg-[#25D366] text-white flex items-center gap-2 shadow-lg"
      data-testid="whatsapp-cta"
    >
      <MessageCircle size={18}/> Chat with us
      <span onClick={(e)=>{e.preventDefault(); setHidden(true);}} className="ml-1 hover:bg-white/20 rounded p-0.5"><X size={12}/></span>
    </a>
  );
}

const TOUR_STEPS = [
  { selector: '[data-testid="hero-search-form"]', title: "Start your search here", body: "Type your city or college and hit search to see verified PGs near you." },
  { selector: '[data-testid="nav-properties"]', title: "Browse all PGs", body: "Filter by gender, budget, food, AC — even by college distance." },
  { selector: '[data-testid="nav-list-property"]', title: "Are you an owner?", body: "List your PG in 4 minutes. Free until you accept your first booking." },
  { selector: '[data-testid="whatsapp-cta"]', title: "Stuck somewhere?", body: "Tap here to chat with our team on WhatsApp — answers within 30 mins." },
];

export function OnboardingTour({ enabled = true }) {
  const loc = useLocation();
  const [step, setStep] = useState(-1);
  const [rect, setRect] = useState(null);
  const intervalRef = useRef();

  useEffect(() => {
    if (!enabled) return;
    if (shouldSuppress(loc.pathname)) { setStep(-1); return; }
    // Tour selectors point to landing page elements only — show only on "/".
    if (loc.pathname !== "/") { setStep(-1); return; }
    if (localStorage.getItem(TOUR_KEY)) return;
    // Delay 8s so first-paint and hero feel snappy + don't compete with chat widget.
    const t = setTimeout(() => setStep(0), 8000);
    return () => clearTimeout(t);
  }, [enabled, loc.pathname]);

  useEffect(() => {
    if (step < 0) return;
    const update = () => {
      const el = document.querySelector(TOUR_STEPS[step].selector);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        setRect(null);
      }
    };
    update();
    intervalRef.current = setInterval(update, 500);
    return () => clearInterval(intervalRef.current);
  }, [step]);

  const next = () => {
    if (step >= TOUR_STEPS.length - 1) finish();
    else setStep(step + 1);
  };
  const finish = () => {
    localStorage.setItem(TOUR_KEY, "1");
    setStep(-1); setRect(null);
  };

  if (step < 0) return null;
  const s = TOUR_STEPS[step];

  return (
    <div className="fixed inset-0 z-[55] pointer-events-none" data-testid="onboarding-tour">
      {/* Dim overlay with cut-out */}
      {rect && (
        <div className="absolute inset-0 pointer-events-auto" onClick={finish}>
          <svg width="100%" height="100%" style={{ position: "absolute", inset: 0 }}>
            <defs>
              <mask id="hole">
                <rect width="100%" height="100%" fill="white"/>
                <rect x={rect.left - 6} y={rect.top - 6} width={rect.width + 12} height={rect.height + 12} rx={6} fill="black"/>
              </mask>
            </defs>
            <rect width="100%" height="100%" fill="rgba(15,31,23,0.65)" mask="url(#hole)"/>
            <rect x={rect.left - 6} y={rect.top - 6} width={rect.width + 12} height={rect.height + 12} rx={6} fill="none" stroke="#2ED573" strokeWidth="3" strokeDasharray="6 4"/>
          </svg>
        </div>
      )}
      {/* Step card */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 brut-card p-5 bg-[#FDFCF8] max-w-md w-[92%] pointer-events-auto" data-testid="tour-card">
        <div className="flex justify-between items-center gap-2 mb-1">
          <span className="brut-tag lime">Step {step + 1} / {TOUR_STEPS.length}</span>
          <button onClick={finish} className="brut-border p-1 bg-white" data-testid="tour-skip"><X size={12}/></button>
        </div>
        <h4 className="font-display font-black text-xl mt-1">{s.title}</h4>
        <p className="text-sm text-[#484848] mt-1">{s.body}</p>
        <div className="flex gap-2 mt-3">
          {step > 0 && <button className="brut-btn white !py-2 !px-3 !text-xs" onClick={()=>setStep(step-1)}>Back</button>}
          <button className="brut-btn flex-1 !text-sm" onClick={next} data-testid="tour-next">{step >= TOUR_STEPS.length - 1 ? "Got it!" : "Next →"}</button>
        </div>
      </div>
    </div>
  );
}

export function startTour() {
  localStorage.removeItem(TOUR_KEY);
  window.location.reload();
}
